import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowUp, Check, FileText, Mail, MessageSquare, Pencil,
  RefreshCw, Send, Sparkles, X,
} from "lucide-react";
import ClientDocuments from "@/components/ClientDocuments";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import RecommendationView from "@/components/RecommendationView";
import ApprovedBlueprint from "@/components/ApprovedBlueprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ICONS = {
  project_created: Sparkles,
  email_sent: Mail,
  email_failed: Mail,
  client_update: MessageSquare,
  producer_note: MessageSquare,
  recommendation_regenerated: RefreshCw,
  brief_updated: Pencil,
  sent_for_review: Send,
  blueprint_approved: Check,
  document_shared: FileText,
  document_comment: MessageSquare,
  document_status: Check,
};

const STAGE_COPY = {
  exploring: {
    label: "Live Brief",
    line: "Keep shaping it. Nothing is locked until you say so.",
  },
  producer_review: {
    label: "With your Producer",
    line: "Your Producer is personally reviewing everything. You\u2019ll hear back within one business day.",
  },
  approved: {
    label: "Approved · Version 1",
    line: "The real work begins. Keep the conversation going anytime.",
  },
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function TimelineSection({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="mt-16 md:mt-20 max-w-[640px] mx-auto text-left" data-testid="project-timeline">
      <div className="eyebrow eyebrow-denim mb-6 text-center">Project Timeline</div>
      <div className="border-l border-bnm pl-6 space-y-6">
        {[...events].reverse().map((e) => {
          const Icon = EVENT_ICONS[e.type] || Sparkles;
          return (
            <div key={e.id} className="relative" data-testid="timeline-event" data-event-type={e.type}>
              <span className="absolute -left-[31px] top-1 w-2.5 h-2.5 rounded-full bg-white border border-denim-bnm" />
              <div className="flex items-center gap-2 text-ink text-[14.5px]">
                <Icon className="w-3.5 h-3.5 text-muted-ink shrink-0" />
                <span className={e.type === "email_failed" ? "text-muted-ink" : ""}>{e.label}</span>
              </div>
              <div className="text-[12px] text-muted-ink mt-1">{fmtDate(e.created_at)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const BRIEF_FIELDS = [
  { key: "brief", label: "The Brief", multiline: true },
  { key: "timeline", label: "Timeline" },
  { key: "budget", label: "Budget" },
  { key: "location", label: "Location" },
  { key: "notes", label: "Notes", multiline: true },
];

function LiveBrief({ project, onProject, locked }) {
  const [editing, setEditing] = useState(null); // field key
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (key) => {
    setEditing(key);
    setValue(project[key] || "");
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/projects/${project.id}/brief`, { [editing]: value });
      onProject(data.project);
      setEditing(null);
    } catch (e) {
      console.error("brief edit failed", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10 md:mt-12 max-w-[720px] mx-auto text-left bnm-card p-6 md:p-8" data-testid="live-brief">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow eyebrow-muted">{locked ? "The Brief · Version 1" : "Live Working Brief"}</div>
        {!locked && <div className="text-[11.5px] text-muted-ink">Tap any line to refine it</div>}
      </div>
      <div className="divide-y divide-[#f0f0f0]">
        {BRIEF_FIELDS.map(({ key, label, multiline }) => {
          const val = project[key];
          if (!val && key !== "brief") return null;
          const isEditing = editing === key;
          return (
            <div key={key} className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.08em] text-muted-ink mb-1.5">{label}</div>
                  {isEditing ? (
                    <div>
                      {multiline ? (
                        <textarea
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          rows={3}
                          autoFocus
                          className="w-full rounded-xl border border-bnm px-4 py-3 text-[15px] text-ink leading-[1.6] resize-none focus:outline-none focus:border-black"
                          data-testid={`brief-edit-${key}`}
                        />
                      ) : (
                        <input
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          autoFocus
                          className="w-full rounded-xl border border-bnm px-4 py-2.5 text-[15px] text-ink focus:outline-none focus:border-black"
                          data-testid={`brief-edit-${key}`}
                        />
                      )}
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={save}
                          disabled={saving}
                          className="inline-flex items-center gap-1.5 text-[13px] px-4 py-1.5 rounded-full bg-ink text-white disabled:opacity-40"
                          data-testid={`brief-save-${key}`}
                        >
                          <Check className="w-3.5 h-3.5" /> {saving ? "Saving\u2026" : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="inline-flex items-center gap-1.5 text-[13px] px-4 py-1.5 rounded-full border border-bnm text-ink"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-ink text-[15.5px] md:text-[16.5px] leading-[1.7]" data-testid={`brief-value-${key}`}>
                      {val || <span className="text-muted-ink">Not set</span>}
                    </p>
                  )}
                </div>
                {!locked && !isEditing && (
                  <button
                    type="button"
                    onClick={() => startEdit(key)}
                    className="text-muted-ink hover:text-ink transition-colors shrink-0 mt-5"
                    aria-label={`Edit ${label}`}
                    data-testid={`brief-edit-btn-${key}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConversationSection({ project, onProject, locked }) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionError, setActionError] = useState(null);
  const updates = project.updates || [];

  const sendNote = async () => {
    const text = note.trim();
    if (text.length < 2 || sending) return;
    setSending(true);
    setActionError(null);
    try {
      await axios.post(`${API}/projects/${project.id}/updates`, { text });
      const { data } = await axios.get(`${API}/projects/${project.id}`);
      onProject(data.project);
      setNote("");
    } catch (e) {
      console.error("update failed", e);
      setActionError("Your note didn\u2019t reach your Producer. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setActionError(null);
    try {
      const { data } = await axios.post(`${API}/projects/${project.id}/regenerate`);
      onProject(data.project);
    } catch (e) {
      console.error("regenerate failed", e);
      setActionError("Your Producer couldn\u2019t rework the plan just now. Please try again in a moment.");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="mt-16 md:mt-20 max-w-[640px] mx-auto text-left" data-testid="project-conversation">
      <div className="eyebrow eyebrow-denim mb-6 text-center">The Conversation Continues</div>
      {updates.length > 0 && (
        <div className="space-y-4 mb-8">
          {updates.map((u) => (
            <div
              key={u.id}
              className={`bnm-card p-5 ${u.author === "producer" ? "border-l-2 border-l-denim-bnm" : ""}`}
              data-testid="project-update"
              data-author={u.author || "client"}
            >
              {u.author === "producer" && (
                <div className="eyebrow eyebrow-denim mb-2">Your Producer</div>
              )}
              <p className="text-ink text-[15px] leading-[1.7]">{u.text}</p>
              <div className="text-[12px] text-muted-ink mt-2">{fmtDate(u.created_at)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="relative">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendNote();
            }
          }}
          rows={3}
          placeholder="New ideas, corrections, references, anything at all. Your Producer reads everything."
          className="w-full rounded-2xl border border-bnm bg-white px-5 py-4 pr-14 text-[15px] text-ink leading-[1.6] resize-none focus:outline-none focus:border-black transition-colors"
          data-testid="project-note-input"
        />
        <button
          type="button"
          onClick={sendNote}
          disabled={note.trim().length < 2 || sending}
          className="absolute right-3 bottom-4 w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center disabled:opacity-30 transition-opacity"
          aria-label="Send note"
          data-testid="project-note-send"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      </div>
      {!locked && (
        <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
          <button
            type="button"
            onClick={regenerate}
            disabled={regenerating}
            className="btn-ghost disabled:opacity-50"
            data-testid="project-regenerate"
          >
            <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
            <span>{regenerating ? "Your Producer is rethinking\u2026" : "Ask for fresh thinking"}</span>
          </button>
          <p className="text-[12.5px] text-muted-ink leading-[1.6]">
            Your Producer folds every note into the recommendation. Previous versions are kept.
          </p>
        </div>
      )}
      {actionError && (
        <p className="mt-4 text-[13.5px] text-[#b3261e] leading-[1.6]" data-testid="project-action-error">
          {actionError}
        </p>
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReview, setSendingReview] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects/${id}`);
        setProject(data.project);
      } catch (e) {
        setError(e?.response?.status === 403 ? "This project belongs to another account." : "Project not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, authLoading]);

  const stage = project?.stage || "exploring";
  const stageCopy = STAGE_COPY[stage] || STAGE_COPY.exploring;
  const locked = stage !== "exploring";

  const readyForReview = async () => {
    if (sendingReview) return;
    setSendingReview(true);
    try {
      const { data } = await axios.post(`${API}/projects/${id}/ready-for-review`);
      setProject(data.project);
    } catch (e) {
      console.error("ready for review failed", e);
    } finally {
      setSendingReview(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />
      <main className="flex-1">
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-24 md:pb-40">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-ink hover:text-ink transition-colors"
            data-testid="project-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            All projects
          </Link>

          {loading && <div className="mt-16 text-muted-ink text-sm text-center">Loading your project…</div>}

          {!loading && error && (
            <div className="mt-16 max-w-[520px] mx-auto text-center" data-testid="project-error">
              <p className="text-muted-ink text-[16px] leading-[1.7]">{error}</p>
            </div>
          )}

          {!loading && project && (
            <div className="mt-10 md:mt-14 text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="eyebrow eyebrow-denim">Project Workspace</div>
                <span
                  className="text-[11px] tracking-[0.06em] uppercase text-muted-ink border border-bnm rounded-full px-2.5 py-0.5"
                  data-testid="project-stage-badge"
                  data-stage={stage}
                >
                  {stageCopy.label}
                </span>
              </div>
              <p className="mt-4 text-[13.5px] text-muted-ink max-w-[440px] mx-auto leading-[1.6]" data-testid="project-stage-line">
                {stageCopy.line}
              </p>

              {stage === "approved" && project.approved_blueprint ? (
                <div className="mt-14 md:mt-16">
                  <ApprovedBlueprint bp={project.approved_blueprint} note={project.approved_blueprint_note} />
                </div>
              ) : (
                project.recommendation && (
                  <div className="mt-14 md:mt-16">
                    <RecommendationView rec={project.recommendation} user={user} />
                  </div>
                )
              )}

              <ClientDocuments project={project} onProject={setProject} />

              <LiveBrief project={project} onProject={setProject} locked={locked} />

              {stage === "exploring" && (
                <div className="mt-12 max-w-[520px] mx-auto">
                  <button
                    type="button"
                    onClick={readyForReview}
                    disabled={sendingReview}
                    className="btn-denim disabled:opacity-50"
                    data-testid="ready-for-review"
                  >
                    <Send className="w-4 h-4" />
                    <span>{sendingReview ? "Sending to your Producer\u2026" : "Ready for Producer Review"}</span>
                  </button>
                  <p className="mt-4 text-[12.5px] text-muted-ink leading-[1.6]">
                    When the brief feels complete, send it over. Your Producer reviews everything personally and returns with your Approved Creative Blueprint.
                  </p>
                </div>
              )}

              <ConversationSection project={project} onProject={setProject} locked={locked} />
              <TimelineSection events={project.activity} />
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
